/**
 * A one-use link for a client who lost the password (owner, 2026-10-14).
 *
 * The administrator's password is asked again: the link hands over the
 * account. It works once, for 24 hours, and ends every session the client had.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { issueClientPasswordReset } from "@/shared/api";
import type { PlatformClientResponse } from "@/shared/api";

const FIELD =
  "min-h-9 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

export function ClientPasswordReset({ client }: { readonly client: PlatformClientResponse }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const issue = useMutation({
    mutationFn: async () => {
      const { data, response } = await issueClientPasswordReset({
        path: { tenant_id: client.tenant_id, account_id: client.account_id },
        body: { current_password: password },
      });
      if (response?.ok !== true || data === undefined) {
        throw new Error(response?.status === 403 ? "password" : "refused");
      }
      return `${window.location.origin}/reset-password#token=${encodeURIComponent(data.token)}`;
    },
    onSettled: () => { setPassword(""); },
  });

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={() => { setOpen(true); }}>
        Сбросить пароль
      </Button>
    );
  }
  if (issue.data !== undefined) {
    const link = issue.data;
    return (
      <div className="flex flex-col gap-2" data-testid="password-reset-link">
        <span className="text-helper-sm">Отправьте клиенту. Ссылка одноразовая, действует 24 часа.</span>
        <input aria-label="Ссылка для нового пароля" className={`${FIELD} w-full font-mono`} readOnly value={link} onFocus={(event) => { event.target.select(); }} />
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(link).then(() => { setCopied(true); });
            }}
          >
            {copied ? "Скопировано" : "Скопировать"}
          </Button>
          <Button size="sm" onClick={() => { setOpen(false); issue.reset(); setCopied(false); }}>Готово</Button>
        </div>
      </div>
    );
  }
  return (
    <form className="flex flex-col gap-2" onSubmit={(event) => { event.preventDefault(); if (!issue.isPending) issue.mutate(); }}>
      <label className="flex flex-col gap-1">
        <span className="text-helper-sm">Ваш пароль</span>
        <input aria-label="Ваш пароль" className={`${FIELD} w-40`} type="password" autoComplete="current-password" required value={password} onChange={(event) => { setPassword(event.target.value); }} />
      </label>
      <span className="text-helper-sm">Клиент получит ссылку, задаст новый пароль, и все его входы будут закрыты.</span>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={issue.isPending || password === ""}>Получить ссылку</Button>
        <Button size="sm" onClick={() => { setOpen(false); }}>Отмена</Button>
      </div>
      {issue.isError && (
        <span role="alert" className="text-helper-sm text-status-warning">
          {issue.error.message === "password" ? "Пароль не подошёл." : "Ссылка не создана: у счёта нет владельца с доступом."}
        </span>
      )}
    </form>
  );
}
